import { Buffer } from 'buffer';
/**
 * KaitaiStream is an implementation of Kaitai Struct API for JavaScript.
 *
 * It's based on KaitaiStream.js - https://github.com/kaitai-io/kaitai_struct_javascript_runtime/blob/master/KaitaiStream.js that comes with the following license
 *
 * @license Copyright 2012-2016 Ilmari Heikkinen Copyright 2016-2020 Kaitai Project licensed under Apache License, Version 2.0 (the "License");
 *
 * Implementation has been translated to Typescript and modified to fit our needs
 */
export class KaitaiStream {
    /**
     * @param arrayBuffer ArrayBuffer to read from.
     * @param byteOffset Offset from arrayBuffer beginning for the KaitaiStream.
     * @param isBigEndian
     */
    constructor(arrayBuffer, byteOffset = 0, isBigEndian = true) {
        this._bitsLeft = 8;
        /**
            Virtual byte length of the KaitaiStream backing buffer.
            Updated to be max of original buffer size and last written size.
            If dynamicSize is false is set to buffer size.
            @type {number}
            */
        this._byteLength = 0;
        this._byteOffset = byteOffset;
        if (arrayBuffer instanceof ArrayBuffer ||
            (arrayBuffer && arrayBuffer.constructor.name === 'SharedArrayBuffer')) {
            this.buffer = arrayBuffer; // TODO check SharedArrayBuffer can be associated as ArrayBuffer
        }
        else if (typeof arrayBuffer === 'object') {
            this.dataView = arrayBuffer;
            if (byteOffset) {
                this._byteOffset += byteOffset;
            }
        }
        else {
            this.buffer = new ArrayBuffer(arrayBuffer || 1);
        }
        this._isBigEndian = isBigEndian;
        this._pos = 0;
        this.alignToByte();
    }
    get byteLeft() {
        return this.size - this._pos;
    }
    set pos(v) {
        this._pos = v;
        this.alignToByte();
    }
    get pos() {
        return this._pos;
    }
    /**
          Set/get the backing ArrayBuffer of the KaitaiStream object.
          The setter updates the DataView to point to the new buffer.
          @type {Object}
          */
    get buffer() {
        this._trimAlloc();
        return this._buffer;
    }
    get toBytes() {
        let length = this._dataView.byteLength;
        if (this._bitsLeft < 8) {
            length += 1;
        }
        const a = new Uint8Array(length);
        // console.debug(this._dataView)
        for (let i = 0; i < length; i++)
            a[i] = this._dataView.getUint8(i);
        return a;
    }
    set buffer(v) {
        this._buffer = v;
        this._dataView = new DataView(this._buffer, this._byteOffset);
        this._byteLength = this._buffer.byteLength;
    }
    get isBigEndian() {
        return this._isBigEndian;
    }
    get isLittleEndian() {
        return !this._isBigEndian;
    }
    /**
        Set/get the byteOffset of the KaitaiStream object.
        The setter updates the DataView to point to the new byteOffset.
        @type {number}
        */
    get byteOffset() {
        return this._byteOffset;
    }
    set byteOffset(v) {
        this._byteOffset = v;
        this._dataView = new DataView(this._buffer, this._byteOffset);
        this._byteLength = this._buffer.byteLength;
    }
    /**
        Set/get the backing DataView of the KaitaiStream object.
        The setter updates the buffer and byteOffset to point to the DataView values.
        @type {Object}
        */
    get dataView() {
        return this._dataView;
    }
    set dataView(v) {
        this._byteOffset = v.byteOffset;
        this._buffer = v.buffer;
        this._dataView = new DataView(this._buffer, this._byteOffset);
        this._byteLength = this._byteOffset + v.byteLength;
    }
    /**
        Returns the byte length of the KaitaiStream object.
        @type {number}
        */
    get size() {
        return this._byteLength - this._byteOffset;
    }
    /**
     * Number of bits left to read/write for the current byte (when read bit by bit instead of bytes)
     * Number between 8 and 1
     * @deprecated
     */
    get bitsLeft() {
        return this._bitsLeft;
    }
    /**
     * @deprecated will be removed in future release
     */
    get bits() {
        return this._getBitsFromCurrentPosition();
    }
    static bytesStripRight(data, padByte) {
        let newLen = data.length;
        while (data[newLen - 1] === padByte)
            newLen--;
        return data.slice(0, newLen);
    }
    static bytesTerminate(data, term, include = false) {
        let newLen = 0;
        const maxLen = data.length;
        while (newLen < maxLen && data[newLen] !== term)
            newLen++;
        if (include && newLen < maxLen)
            newLen++;
        return data.slice(0, newLen);
    }
    /**
     * @deprecated will be removed in future major release
     * @param arr
     * @param encoding
     * @returns
     */
    /* istanbul ignore next */
    static bytesToStr(arr, encoding) {
        if (encoding == null || encoding.toLowerCase() === 'ascii') {
            return KaitaiStream.createStringFromArray(arr);
        }
        else {
            if (typeof TextDecoder === 'function') {
                // we're in the browser that supports TextDecoder
                return new TextDecoder(encoding).decode(arr);
            }
            else {
                // probably we're in node.js
                // check if it's supported natively by node.js Buffer
                // see https://github.com/nodejs/node/blob/master/lib/buffer.js#L187 for details
                const bufferEncoding = kaitaiEncodingtoBufferEncoding(encoding);
                return Buffer.from(arr).toString(bufferEncoding);
            }
        }
    }
    /**
     * @deprecated
     * @param a
     * @param b
     * @returns
     */
    /* istanbul ignore next */
    static mod(a, b) {
        if (b <= 0)
            throw new Error('mod divisor <= 0');
        let r = a % b;
        if (r < 0)
            r += b;
        return r;
    }
    /**
     * @deprecated
     * @param a
     * @param b
     * @returns
     */
    /* istanbul ignore next */
    static arrayMin(arr) {
        let min = arr[0];
        let x;
        for (let i = 1, n = arr.length; i < n; ++i) {
            x = arr[i];
            if (x < min)
                min = x;
        }
        return min;
    }
    /**
     * @deprecated
     * @param a
     * @param b
     * @returns
     */
    /* istanbul ignore next */
    static arrayMax(arr) {
        let max = arr[0];
        let x;
        for (let i = 1, n = arr.length; i < n; ++i) {
            x = arr[i];
            if (x > max)
                max = x;
        }
        return max;
    }
    /**
     * @deprecated
     * @param a
     * @param b
     * @returns
     */
    /* istanbul ignore next */
    static byteArrayCompare(a, b) {
        if (a === b)
            return 0;
        const al = a.length;
        const bl = b.length;
        const minLen = al < bl ? al : bl;
        for (let i = 0; i < minLen; i++) {
            const cmp = a[i] - b[i];
            if (cmp !== 0)
                return cmp;
        }
        // Reached the end of at least one of the arrays
        if (al === bl) {
            return 0;
        }
        else {
            return al - bl;
        }
    }
    /**
      * @deprecated
      * Creates an array from an array of character codes.
        Uses String.fromCharCode in chunks for memory efficiency and then concatenates
        the resulting string chunks.
        @param {array} array Array of character codes.
        @return {string} String created from the character codes.
      */
    /* istanbul ignore next */
    static createStringFromArray(array) {
        // TODO
        const chunk_size = 0x8000;
        const chunks = [];
        for (let i = 0; i < array.length; i += chunk_size) {
            chunks.push(String.fromCharCode.apply(null, array.subarray(i, i + chunk_size)));
        }
        return chunks.join('');
    }
    // ========================================================================
    // Byte array processing
    // ========================================================================
    /**
     * @deprecated
     * @param data
     * @param key
     * @returns
     */
    /* istanbul ignore next */
    static processXorOne(data, key) {
        const r = new Uint8Array(data.length);
        const dl = data.length;
        for (let i = 0; i < dl; i++)
            r[i] = data[i] ^ key;
        return r;
    }
    /**
     * @deprecated
     * @param data
     * @param key
     * @returns
     */
    /* istanbul ignore next */
    static processXorMany(data, key) {
        const r = new Uint8Array(data.length);
        const dl = data.length;
        const kl = key.length;
        let ki = 0;
        for (let i = 0; i < data.length; i++) {
            r[i] = data[i] ^ key[ki];
            ki++;
            if (ki >= kl)
                ki = 0;
        }
        return r;
    }
    /**
     * @deprecated
     * @param data
     * @param amount
     * @param groupSize
     * @returns
     */
    /* istanbul ignore next */
    static processRotateLeft(data, amount, groupSize) {
        if (groupSize !== 1)
            throw new Error('unable to rotate group of ' + groupSize + ' bytes yet');
        const mask = groupSize * 8 - 1;
        const antiAmount = -amount & mask;
        const r = new Uint8Array(data.length);
        for (let i = 0; i < data.length; i++)
            r[i] = ((data[i] << amount) & 0xff) | (data[i] >> antiAmount);
        return r;
    }
    getStreamSize() {
        return this._buffer.byteLength;
    }
    alignToByte() {
        this._bitsLeft = 8;
    }
    _getBitsFromCurrentPosition() {
        if (this._pos < this._byteLength) {
            return this._dataView.getUint8(this._pos);
        }
        else {
            return 0;
        }
    }
    _decreaseBitsLeft(val) {
        if (val < 0) {
            val = 0;
        }
        this._bitsLeft -= val;
        if (this._bitsLeft <= 0) {
            this.pos++;
        }
    }
    /**
        Internal function to trim the KaitaiStream buffer when required.
        Used for stripping out the extra bytes from the backing buffer when
        the virtual byteLength is smaller than the buffer byteLength (happens after
        growing the buffer with writes and not filling the extra space completely).
        @return {null}
        */
    _trimAlloc() {
        if (this._byteLength === this._buffer.byteLength) {
            return;
        }
        const buf = new ArrayBuffer(this._byteLength);
        const dst = new Uint8Array(buf);
        const src = new Uint8Array(this._buffer, 0, dst.length);
        dst.set(src);
        this.buffer = buf;
    }
    // ========================================================================
    // Stream positioning
    // ========================================================================
    /**
        Returns true if the KaitaiStream seek pointer is at the end of buffer and
        there's no more data to read.
        @return {boolean} True if the seek pointer is at the end of the buffer.
        */
    isEof() {
        return this.pos >= this.size;
    }
    forward(relativePosition) {
        this.seek(this.pos + relativePosition);
        return this;
    }
    /**
        Sets the KaitaiStream read/write position to given position.
        Clamps between 0 and KaitaiStream length.
        @param {number} pos Position to seek to.
        @return {null}
        */
    seek(pos) {
        const npos = Math.max(0, Math.min(this.size, pos));
        this.pos = isNaN(npos) || !isFinite(npos) ? 0 : npos;
    }
    forwardBits(n) {
        const bytesLength = Math.floor(n / 8);
        const bitsLength = n % 8;
        const newBitLeft = this._bitsLeft - bitsLength;
        if (newBitLeft <= 0) {
            this.pos += bytesLength + 1;
            this._bitsLeft = 8 + newBitLeft;
        }
        else {
            this.pos += bytesLength;
            this._bitsLeft = newBitLeft;
        }
        return this;
    }
    setBitPosition(bitPosition) {
        bitPosition = Math.max(0, bitPosition);
        const bytePosition = Math.floor(bitPosition / 8);
        this.pos = bytePosition;
        this.forwardBits(bitPosition % 8);
    }
    getBitPosition() {
        return this.pos * 8 + (8 - this._bitsLeft);
    }
    // ========================================================================
    // Internal implementation details
    // ========================================================================
    /**
        Maps a Uint8Array into the KaitaiStream buffer.
        Nice for quickly reading in data.
        @param {number} length Number of elements to map.
        @return {Object} Uint8Array to the KaitaiStream backing buffer.
        */
    mapUint8Array(length) {
        length |= 0;
        if (this.pos + length > this.size) {
            throw new EOFError(length, this.size - this.pos);
        }
        const arr = new Uint8Array(this._buffer, this.byteOffset + this.pos, length);
        this._pos += length;
        return arr;
    }
}
/**
    Dependency configuration data. Holds urls for (optional) dynamic loading
    of code dependencies from a remote server. For use by (static) processing functions.
    Caller should the supported keys to the asset urls as needed.
    NOTE: `depUrls` is a static property of KaitaiStream (the factory),like the various
          processing functions. It is NOT part of the prototype of instances.
    @type {Object}
    */
KaitaiStream.depUrls = {
    // processZlib uses this and expected a link to a copy of pako.
    // specifically the pako_inflate.min.js script at:
    // https://raw.githubusercontent.com/nodeca/pako/master/dist/pako_inflate.min.js
    zlib: undefined,
};
/**
    Native endianness. Either KaitaiStream.BIG_ENDIAN or KaitaiStream.LITTLE_ENDIAN
    depending on the platform endianness.
    @type {boolean}
   */
KaitaiStream.endianness = new Int8Array(new Int16Array([1]).buffer)[0] > 0;
export class EOFError extends Error {
    constructor(bytesReq, bytesAvail) {
        super('requested ' +
            bytesReq +
            ' bytes, but only ' +
            bytesAvail +
            ' bytes available');
        this.bytesReq = bytesReq;
        this.bytesAvail = bytesAvail;
    }
}
export class UnexpectedDataError extends Error {
    constructor(expected, actual) {
        super('expected [' + expected + '], but got [' + actual + ']');
        this.expected = expected;
        this.actual = actual;
    }
}
export class UndecidedEndiannessError extends Error {
    constructor() {
        super('UndecidedEndiannessError');
    }
}
function kaitaiEncodingtoBufferEncoding(encoding) {
    encoding = encoding.toLowerCase();
    switch (encoding) {
        case 'utf8':
        case 'utf-8':
        case 'ucs2':
        case 'ucs-2':
        case 'utf16le':
        case 'utf-16le':
            return encoding;
        default:
            // unsupported encoding, we'll have to resort to iconv-lite
            // if (typeof KaitaiStream.iconvlite === 'undefined')
            //     KaitaiStream.iconvlite = require('iconv-lite');
            // return KaitaiStream.iconvlite.decode(arr, encoding);
            throw new Error(`Unsupported encoding: ${encoding}`);
    }
}
